<template>
  <DefaultCrudView
    @create-new="newPipeline"
    @navigate-to-routines="navigateToRoutines"
    :breadcrumbsItems="breadcrumbs"
    :actions="crudActions"
    containerLarge
  >
    <template slot="content">

      <v-data-table
        :headers="headers"
        :items="pipelines"
        class="elevation-1"
        :loading="loading"
        :options.sync="options"
        :server-items-length="itemsLength"
        :footer-props="$defaultDataTableFooterProps"
      >
        <template v-slot:item.tenant_id="{ item }">
          {{ getTenantTitle(item.tenant_id) }}
        </template>

        <template v-slot:item.built_in="{ item }">
          <v-chip
            :color="item.built_in ? 'green' : 'grey'"
            text-color="white"
            small
          >
            {{ item.built_in ? $t('yes') : $t('no') }}
          </v-chip>
        </template>

        <template v-slot:item.actions="{ item }">
          <v-tooltip left v-if="canEditDeletePipeline(item)">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click="editPipeline(item)"
              >
                mdi-pencil
              </v-icon>
            </template>
            <span>{{ $t('edit') }}</span>
          </v-tooltip>

          <v-tooltip left v-if="canEditDeletePipeline(item)">
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                @click.stop="deletePipeline(item)"
              >
                mdi-delete
              </v-icon>
            </template>
            <span>{{ $t('delete') }}</span>
          </v-tooltip>

          <v-tooltip left>
            <template v-slot:activator="{ on, attrs }">
              <v-icon
                v-bind="attrs"
                v-on="on"
                class="mr-2"
                  @click="openPipeStep(item)"
              >
                mdi-tune-vertical
              </v-icon>
            </template>
            <span>{{ $t('manage_pipesteps') }}</span>
          </v-tooltip>

        </template>

      </v-data-table>

      <DefaultModal 
        :title="getModalTitle()" 
        ref="modal" 
        v-on:submit="save"
      >
        <template v-slot:content>
          <v-container>
            <v-form ref="form" v-model="valid">
              <v-row>
                <v-col cols="12" md="6" v-if="!checkUserIsMaster()">
                  <v-select
                    v-model="selectedPipeline.tenant_id"
                    :items="tenantOptions"
                    item-text="title"
                    item-value="tenant_id"
                    :label="$t('tenant')"
                    :rules="[v => !!v || $t('tenant_required')]"
                    required
                    outlined
                    dense
                  ></v-select>
                </v-col>
                <v-col cols="12" md="6">
                  <v-text-field
                    v-model="selectedPipeline.pipeline_key"
                    :label="$t('pipeline_key')"
                    :rules="[v => !!v || $t('pipeline_key_required')]"
                    required
                    outlined
                    dense
                  ></v-text-field>
                </v-col>
              </v-row>
            </v-form>
          </v-container>
        </template>
      </DefaultModal>

      <!-- Confirmation Dialog -->
      <v-dialog v-model="confirmDialog" max-width="400">
        <v-card>
          <v-card-title class="headline">{{ $t('confirm') }}</v-card-title>
          <v-card-text>
            {{ $t('confirm_delete') }}
          </v-card-text>
          <v-card-actions>
            <v-spacer></v-spacer>
            <v-btn color="grey darken-1" text @click="confirmDialog = false">
              {{ $t('close') }}
            </v-btn>
            <v-btn color="red darken-1" text @click="confirmDelete">
              {{ $t('delete') }}
            </v-btn>
          </v-card-actions>
        </v-card>
      </v-dialog>

    </template>
  
  </DefaultCrudView>
</template>

<style scoped>
  .container {
    padding: 0;
  }

  .table-container {
    margin-top: 20px;
  }
</style>

<script>
import { mapActions, mapGetters } from 'vuex'

import DefaultCrudView from '@/components/DefaultCrudView.vue'
import DefaultModal from '@/components/DefaultModal.vue'
import pipelineApi from '@/services/api/pipeline/pipeline.js'
import TenantActions from '@/store/core/tenant/actions-types'
import AppActions from '@/store/app/actions-types'
import UserRoles from '@/store/core/user/user-roles'

export default {
  name: "Pipeline",

  components: {
    DefaultCrudView,
    DefaultModal
  },

  computed: {
    ...mapGetters('app', [
      'getIconForOperationSettingsItem'
    ]),

    ...mapGetters('core/auth', [
      'checkAccessRights'
    ]),

    breadcrumbs() {
      return [
        {
          text: this.$t('pipelines'),
          prependIcon: this.getIconForOperationSettingsItem('pipelines') || 'mdi-pipe',
        },
      ]
    },

    validTenant(pipeline) {
      if (!pipeline) return false

      // Don't allow editing or deletion of built-in pipelines
      if (pipeline.built_in) return false

      // Check if pipeline has a tenant_id (not null, undefined, empty string, or 0)
      const pipelineHasTenant = pipeline.tenant_id !== null &&
                                 pipeline.tenant_id !== undefined &&
                                 pipeline.tenant_id !== '' &&
                                 pipeline.tenant_id !== 0

      if (this.checkUserIsMaster()) {
        // Master can only edit/delete pipelines without tenant_id (null, undefined, empty, or 0)
        return !pipelineHasTenant
      } else {
        // Admin can edit/delete any pipeline that has a tenant_id
        return pipelineHasTenant
      }
    },

    tenantOptions() {
      if (this.checkUserIsMaster()) {
        // Master users can see tenants without tenant_id (system-level tenants)
        return this.tenants.filter(tenant => tenant.tenant_id == null)
      } else {
        // Admin users can see tenants where they are admins
        const userData = this.$store.state.core.auth.userData
        if (userData && userData.admins && userData.admins.length > 0) {
          const adminTenantIds = userData.admins.map(admin => admin.tenant_id)
          return this.tenants.filter(tenant => adminTenantIds.includes(tenant.tenant_id))
        }
        return []
      }
    }
  },

  data() {
    return {
      valid: false,
      loading: true,
      pipelines: [],
      options: {
        sortBy: ['pipeline_key'],
        sortDesc: [false],
      },
      itemsLength: 0,
      crudActions: [
        {
          text: "routines",
          eventToEmit: "navigate-to-routines",
        },
        {
          text: "create_pipeline",
          eventToEmit: "create-new",
        }
      ],
      tenants: [],
      confirmDialog: false,
      pipelineToDelete: null,
      selectedPipeline: {
        tenant_id: null,
        pipeline_key: ''
      },
      headers: [
        {
          text: this.$t('pipeline_id'),
          align: 'start',
          sortable: true,
          value: 'pipeline_id',
        },
        {
          text: this.$t('tenant'),
          value: 'tenant_id',
          sortable: true,
        },
        {
          text: this.$t('pipeline_key'),
          value: 'pipeline_key',
          sortable: true,
        },
        {
          text: this.$t('built_in'),
          value: 'built_in',
          sortable: true,
        },
        {
          text: this.$t('actions'),
          value: 'actions',
          sortable: false,
        },
      ]
    }
  },

  mounted() {
    this.resetSelectedPipeline()

    // Load tenants based on user role
    this.loadTenants()
  },

  watch: {
    options: {
      handler () {
        this.reloadData()
      },
      deep: true,
    },
  },
  methods: {
    ...mapActions('core/tenant', [
      TenantActions.GET_ALL_TENANTS
    ]),

    ...mapActions('app', [
      AppActions.OPEN_APP_SUCCESS_MESSAGE,
      AppActions.OPEN_APP_ERROR_MESSAGE
    ]),

    openPipeStep(item) {
      this.$router.push({
        name: 'PipeStep',
        query: { pipeline_id: item.pipeline_id }
      })
    },

    newPipeline() {
      this.resetSelectedPipeline()
      this.$refs.modal.openModal()
    },

    resetSelectedPipeline() {
      let defaultTenantId = null
      
      if (!this.checkUserIsMaster()) {
        // For admin users, get the first available tenant from their admin tenants
        const userData = this.$store.state.core.auth.userData
        if (userData && userData.admins && userData.admins.length > 0) {
          defaultTenantId = userData.admins[0].tenant_id
        }
      }
      
      this.selectedPipeline = {
        tenant_id: defaultTenantId,
        pipeline_key: ''
      }
    },

    checkUserIsMaster() {
      return this.checkAccessRights([UserRoles.USER_MASTER])
    },

    save() {
      if (!this.$refs.form.validate()) {
        return
      }

      this.$refs.modal.errorMessage = null
      this.$refs.modal.errorList = []

      const isEdit = this.selectedPipeline.pipeline_id

      const apiCall = isEdit 
        ? pipelineApi.updatePipeline(this.selectedPipeline.pipeline_id, {
            pipeline_key: this.selectedPipeline.pipeline_key
          })
        : pipelineApi.createPipeline({
            tenant_id: this.selectedPipeline.tenant_id,
            pipeline_key: this.selectedPipeline.pipeline_key
          })

      apiCall
        .then(() => {
          const message = isEdit ? 'Pipeline updated' : 'Pipeline created'
          this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t(message))
          this.$refs.modal.closeModal()
          this.reloadData()
        })
        .catch((error) => {
          console.error('Error saving pipeline:', error)
          this[AppActions.OPEN_APP_ERROR_MESSAGE](error.response?.data?.message || 'Error saving pipeline')
          this.$refs.modal.submitting = false
        })
    },

    reloadData() {
      let options = {...this.$route.query}

      if (this.options.sortBy.length > 0) {
        options.order = this.options.sortBy[0]
      }

      if (this.options.sortDesc.length > 0 && this.options.sortDesc[0]) {
        options.direction = 'DESC'
      }

      options.limit = this.options.itemsPerPage
      options.offset = this.options.itemsPerPage * (this.options.page - 1)
      
      this.loading = true
      
      pipelineApi.getAllPipelines(options)
        .then((response) => {
          const result = response.data
          this.pipelines = result.items || result.data || result || []
          this.itemsLength = result.total || result.count || (result.items ? result.items.length : 0)
          this.loading = false
        })
        .catch((error) => {
          console.error('Error loading pipelines:', error)
          this[AppActions.OPEN_APP_ERROR_MESSAGE]('Error loading pipelines')
          this.loading = false
        })
    },

    editPipeline(pipeline) {
      this.selectedPipeline = { ...pipeline }
      this.$refs.modal.openModal()
    },

    deletePipeline(pipeline) {
      this.pipelineToDelete = pipeline
      this.confirmDialog = true
    },

    confirmDelete() {
      if (this.pipelineToDelete) {
        pipelineApi.deletePipeline(this.pipelineToDelete.pipeline_id)
          .then(() => {
            this[AppActions.OPEN_APP_SUCCESS_MESSAGE](this.$t('Pipeline deleted'))
            this.reloadData()
            this.confirmDialog = false
            this.pipelineToDelete = null
          })
          .catch((error) => {
            console.error('Error deleting pipeline:', error)
            this[AppActions.OPEN_APP_ERROR_MESSAGE]('Error deleting pipeline')
            this.confirmDialog = false
            this.pipelineToDelete = null
          })
      }
    },

    loadTenants() {
      // Load all tenants - filtering will be done in tenantOptions computed property
      this[TenantActions.GET_ALL_TENANTS]()
        .then((response) => {
          // Handle different response structures
          this.tenants = response.items || response.data || response || []
        })
        .catch((error) => {
          console.error('Error loading tenants:', error)
          this[AppActions.OPEN_APP_ERROR_MESSAGE]('Error loading tenants')
        })
    },

    getTenantTitle(id) {
      if (!id) return ''
      const tenant = this.tenants.find(f => f.tenant_id == id)
      return tenant ? tenant.title : ''
    },

    getModalTitle() {
      return this.selectedPipeline.pipeline_id ? this.$t('edit_pipeline') : this.$t('new_pipeline')
    },

    canEditDeletePipeline(pipeline) {
      if (!pipeline) return false
      
      // Check if pipeline has a tenant_id (not null, undefined, empty string, or 0)
      const pipelineHasTenant = pipeline.tenant_id !== null && 
                               pipeline.tenant_id !== undefined && 
                               pipeline.tenant_id !== '' && 
                               pipeline.tenant_id !== 0

      if (this.checkUserIsMaster()) {
        // Master can only edit pipelines without tenant_id (null, undefined, empty, or 0)
        return !pipelineHasTenant
      } else {
        // Admin can edit any pipeline that has a tenant_id
        return pipelineHasTenant
      }
    },

    navigateToRoutines() {
      this.$router.push({ name: 'Routine' })
    }
  }
}
</script>
